import React, { useId } from "react";

// Copy of src/components/HouseIllustration.js from the dobrereal-site repo: change the drawing in both.
// 3D-style illustration of Hišica pri orehu, seen from the back corner so the wooden wellness is
// visible: the gable end with its windows faces the viewer, the back wall (white brick chimney, door)
// recedes to the right and the glazed entry sticks out on the left. The wellness follows a drone photo
// of the house: it stands behind the far end of the back wall, WELL_GAP away from it, its low part
// towards the gable and its taller part behind, with a band of windows above the low roof.
//
// World coordinates (roughly metres):
//   x: 0..L along the front wall, left to right as seen from the drive (x = L is the gable with windows)
//   y: 0..D from the front wall (entry) to the back wall (wellness)
//   z: height above ground
// Controls can be pinned onto the house with markers = [{ id, at: [x, y, z], label, value, active, side, onClick }].

const L = 10; // length of the house
const D = 6.5; // depth (width of the gable end)
const H = 4.6; // wall height
const RIDGE = 8.1; // ridge height
const T = 0.22; // roof thickness
const OV_X = 0.4; // roof overhang past the gables
const OV_Y = 0.55; // roof overhang past the long walls
const SLOPE = (RIDGE - H) / (D / 2);
const EZ = H - OV_Y * SLOPE; // eave height

// Entry (glazed porch in front of the front wall).
const ENTRY = { x1: 5.7, x2: 9.7, depth: 1.6, h: 3.5 };
// Wellness, behind the back wall. Roofs slope down away from the house, so the height depends on y.
const WELL_GAP = 1.2; // free space between the house and the wellness
const WELL_Y = D + WELL_GAP;
const WELL_DEPTH = 3.6;
const WELL_TALL = { x1: 0.1, x2: 2.3, hWall: 3.72, hOut: 3.3 };
const WELL_LOW = { x1: 2.3, hWall: 2.75, hOut: 2.3 };
// The low part steps out towards the gable: narrower next to the house, wider further out.
const WELL_STEP_Y = WELL_Y + 1.2;
const WELL_LOW_NEAR_X = 4.1;
const WELL_LOW_FAR_X = 5.0;
const ROOF_THICKNESS = 0.12;
const wallHeight = ({ hWall, hOut }, y) => hWall + ((hOut - hWall) * (y - WELL_Y)) / WELL_DEPTH;
// White brick chimney next to the gable: a column up the back wall, then a stack through the roof.
const CHIMNEY = { x1: 8.3, x2: 9.1, depth: 0.6, stackY1: D - 1.5, stackY2: D - 0.5, top: 7.75 };
const roofHeightAt = (y) => H + (D - y) * SLOPE;

// Spots on the house for markers.
export const HOUSE_SPOTS = {
  wellness: [3.6, WELL_Y + 1.9, 2.65],
  sauna: [1.2, WELL_Y + 1.9, 3.55],
  entry: [7.7, -0.8, 3.7],
};

// --- Projection ---
const VIEW_W = 610;
const VIEW_H = 445;
const ORIGIN = [95, 318];
const AX = [26, 7];
const BY = [17, -14];
const UP = 23;

// The camera looks at the gable x = L and the back wall y = D: rotate the world so they face it.
const toView = (x, y, z) => [y, L - x, z];
const project = (x, y, z) => {
  const [vx, vy, vz] = toView(x, y, z);
  return [ORIGIN[0] + AX[0] * vx + BY[0] * vy, ORIGIN[1] + AX[1] * vx + BY[1] * vy - UP * vz];
};
const poly = (points) => points.map((q) => project(...q).map((n) => n.toFixed(1)).join(",")).join(" ");

// Direction towards the viewer (in view coordinates): the direction that projects onto a single point.
const VIEWER = (() => {
  const a = 1;
  const b = (-AX[0] / BY[0]) * a;
  return [a, b, (AX[1] * a + BY[1] * b) / UP];
})();
// A face is drawn only when its outward normal (world coordinates) points towards the viewer.
const faces = ([nx, ny, nz]) => ny * VIEWER[0] - nx * VIEWER[1] + nz * VIEWER[2] > 1e-6;

const add = (p, q, k = 1) => [p[0] + q[0] * k, p[1] + q[1] * k, p[2] + q[2] * k];

// Plane given by an origin and two directions; rect() returns the corners of a rectangle in (u, v).
const plane = (origin, u, v) => ({
  origin,
  u,
  v,
  rect: (u1, u2, v1, v2) => [
    add(add(origin, u, u1), v, v1),
    add(add(origin, u, u2), v, v1),
    add(add(origin, u, u2), v, v2),
    add(add(origin, u, u1), v, v2),
  ],
  // SVG matrix mapping (u, v) plane units to the screen, for patterns drawn on the plane.
  matrix: () => {
    const o = project(...origin);
    const pu = project(...add(origin, u));
    const pv = project(...add(origin, v));
    return [pu[0] - o[0], pu[1] - o[1], pv[0] - o[0], pv[1] - o[1], o[0], o[1]].map((n) => n.toFixed(3)).join(" ");
  },
});

const X = [1, 0, 0];
const Y = [0, 1, 0];
const Z = [0, 0, 1];
const E = 0.01; // lifts details off their wall

const GABLE = plane([L + E, 0, 0], Y, Z);
const BACK = plane([0, D + E, 0], X, Z);
const ENTRY_SIDE = plane([ENTRY.x2 + E, 0, 0], Y, Z);
const WELL_OUT = plane([0, WELL_Y + WELL_DEPTH + E, 0], X, Z);
const WELL_BAND = plane([WELL_TALL.x2 + E, 0, 0], Y, Z);

// Shingle rows follow the slope of each wellness roof.
const wellRoofPlane = ({ hWall, hOut }) => {
  const drop = hWall - hOut;
  const length = Math.hypot(WELL_DEPTH, drop);
  return plane([0, WELL_Y, hWall + ROOF_THICKNESS], X, [0, WELL_DEPTH / length, -drop / length]);
};
const CHIMNEY_OUT = plane([0, CHIMNEY.stackY2 + E, 0], X, Z);
const CHIMNEY_END = plane([CHIMNEY.x2 + E, 0, 0], Y, Z);

const slopeLen = Math.hypot(D / 2 + OV_Y, RIDGE - EZ);
const ROOF_BACK = plane([-OV_X, D + OV_Y, EZ], X, [0, -(D / 2 + OV_Y) / slopeLen, (RIDGE - EZ) / slopeLen]);
const ROOF_FRONT = plane([-OV_X, -OV_Y, EZ], X, [0, (D / 2 + OV_Y) / slopeLen, (RIDGE - EZ) / slopeLen]);

// Vertical box faces that can face the viewer, with their outward normals.
function boxSides(x1, x2, y1, y2, z1, z2) {
  return [
    { n: [1, 0, 0], pts: [[x2, y1, z1], [x2, y2, z1], [x2, y2, z2], [x2, y1, z2]] },
    { n: [-1, 0, 0], pts: [[x1, y1, z1], [x1, y2, z1], [x1, y2, z2], [x1, y1, z2]] },
    { n: [0, 1, 0], pts: [[x1, y2, z1], [x2, y2, z1], [x2, y2, z2], [x1, y2, z2]] },
    { n: [0, -1, 0], pts: [[x1, y1, z1], [x2, y1, z1], [x2, y1, z2], [x1, y1, z2]] },
  ];
}

function Face({ n, pts, ...props }) {
  if (!faces(n)) return null;
  return <polygon points={poly(pts)} {...props} />;
}

// Window: white trim, frame, glass with a reflection and a mullion.
function Window({ on, u1, u2, v1, v2, ids, lit, frame = "#7a4a26", mullion = true }) {
  const trim = 0.13;
  const f = 0.09;
  const mid = (u1 + u2) / 2;
  return (
    <g>
      <polygon points={poly(on.rect(u1 - trim, u2 + trim, v1 - trim, v2 + trim))} fill="#eef0ea" />
      <polygon points={poly(on.rect(u1, u2, v1, v2))} fill={frame} />
      <polygon points={poly(on.rect(u1 + f, u2 - f, v1 + f, v2 - f))} fill={`url(#${lit ? ids.glassLit : ids.glass})`} />
      <polygon points={poly(on.rect(u1 + f, u1 + (u2 - u1) * 0.45, v1 + (v2 - v1) * 0.45, v2 - f))} fill="#ffffff" opacity="0.12" />
      {mullion && <polygon points={poly(on.rect(mid - 0.035, mid + 0.035, v1 + f, v2 - f))} fill={frame} />}
      <polygon points={poly(on.rect(u1 - trim - 0.05, u2 + trim + 0.05, v1 - trim - 0.07, v1 - trim))} fill="#c9ccc6" />
    </g>
  );
}

// Shed roof of a wellness part, sloping down away from the house. Each edge in `edges` is a stretch of
// its gable-side outline: { y1, y2, x }, running from the house outwards.
function ShedRoof({ part, x1, edges, felt, shingles, fascia, eave }) {
  const over = 0.1;
  const yOut = WELL_Y + WELL_DEPTH + 0.15;
  const k = (part.hWall - part.hOut) / WELL_DEPTH;
  const z = (y) => wallHeight(part, y);
  const t = ROOF_THICKNESS;
  const last = edges[edges.length - 1];
  const top = [
    [x1 - over, WELL_Y, z(WELL_Y) + t],
    ...edges.flatMap(({ y1, y2, x }) => [[x + over, y1, z(y1) + t], [x + over, y2, z(y2) + t]]),
    [x1 - over, last.y2, z(last.y2) + t],
  ];
  return (
    <g>
      <Face n={[0, 1, 0]} pts={[[x1 - over, yOut, z(yOut)], [last.x + over, yOut, z(yOut)], [last.x + over, yOut, z(yOut) + t], [x1 - over, yOut, z(yOut) + t]]} fill={eave} />
      {edges.map(({ y1, y2, x }) => (
        <Face key={y1} n={[1, 0, 0]} pts={[[x + over, y1, z(y1)], [x + over, y2, z(y2)], [x + over, y2, z(y2) + t], [x + over, y1, z(y1) + t]]} fill={fascia} />
      ))}
      <Face n={[0, k, 1]} pts={top} fill={felt} />
      <Face n={[0, k, 1]} pts={top} fill={shingles} />
    </g>
  );
}

export default function HouseIllustration({ markers = [], lit = false }) {
  const uid = useId().replace(/:/g, "");
  const ids = Object.fromEntries(
    ["glow", "blur", "slab", "gable", "back", "roof", "tilesB", "tilesF", "hooksB", "hooksF", "roofShade", "eaveShadow", "glass", "glassLit", "woodOut", "woodSide", "shinglesTall", "shinglesLow", "brick", "brick2"].map(
      (name) => [name, `${name}${uid}`]
    )
  );

  const gableTop = [L, D / 2, RIDGE - T];
  const yWell = WELL_Y + WELL_DEPTH;
  const roofBackPts = [[-OV_X, D + OV_Y, EZ], [L + OV_X, D + OV_Y, EZ], [L + OV_X, D / 2, RIDGE], [-OV_X, D / 2, RIDGE]];
  const roofFrontPts = [[-OV_X, -OV_Y, EZ], [L + OV_X, -OV_Y, EZ], [L + OV_X, D / 2, RIDGE], [-OV_X, D / 2, RIDGE]];
  const nRoofBack = [0, SLOPE, 1];
  const nRoofFront = [0, -SLOPE, 1];

  const chimneyColumn = boxSides(CHIMNEY.x1, CHIMNEY.x2, D, D + CHIMNEY.depth, 0, EZ - T).map((side) => (
    <Face key={side.n.join()} n={side.n} pts={side.pts} fill={`url(#${side.n[0] ? ids.brick2 : ids.brick})`} />
  ));
  // The stack's lower edge follows the roof slope it comes through.
  const { x1: cx1, x2: cx2, stackY1: cy1, stackY2: cy2, top: ctop } = CHIMNEY;
  const flash = 0.14;

  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} role="img" aria-label="Hišica pri orehu" style={{ display: "block", width: "100%", height: "auto" }}>
        <defs>
          <radialGradient id={ids.glow} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#3b8f8a" stopOpacity="0.3" />
            <stop offset="50%" stopColor="#3b8f8a" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#3b8f8a" stopOpacity="0" />
          </radialGradient>
          <filter id={ids.blur} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="9" />
          </filter>
          <linearGradient id={ids.slab} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#24413f" />
            <stop offset="100%" stopColor="#152a2a" />
          </linearGradient>
          <linearGradient id={ids.gable} x1="0" y1="0" x2="0.4" y2="1">
            <stop offset="0%" stopColor="#f3d582" />
            <stop offset="100%" stopColor="#dcb55c" />
          </linearGradient>
          <linearGradient id={ids.back} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#d2aa52" />
            <stop offset="100%" stopColor="#b08838" />
          </linearGradient>
          <linearGradient id={ids.roof} x1="0" y1="1" x2="0.6" y2="0">
            <stop offset="0%" stopColor="#97421f" />
            <stop offset="100%" stopColor="#c9693f" />
          </linearGradient>
          <linearGradient id={ids.roofShade} x1="0" y1="1" x2="1" y2="0">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.1" />
            <stop offset="55%" stopColor="#ffffff" stopOpacity="0" />
            <stop offset="100%" stopColor="#000000" stopOpacity="0.2" />
          </linearGradient>
          <linearGradient id={ids.eaveShadow} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3a2410" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#3a2410" stopOpacity="0" />
          </linearGradient>
          <linearGradient id={ids.glass} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#5d8e95" />
            <stop offset="55%" stopColor="#21414a" />
            <stop offset="100%" stopColor="#152a31" />
          </linearGradient>
          <linearGradient id={ids.glassLit} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ffe7a8" />
            <stop offset="100%" stopColor="#f2a94a" />
          </linearGradient>
          {/* Vertical spruce boards on the wellness walls */}
          {[[ids.woodOut, WELL_OUT, "#d29f4e"], [ids.woodSide, WELL_BAND, "#e6b863"]].map(([id, on, color]) => (
            <pattern key={id} id={id} patternUnits="userSpaceOnUse" width="0.17" height="1" patternTransform={`matrix(${on.matrix()})`}>
              <rect x="0" y="0" width="0.17" height="1" fill={color} />
              <rect x="0" y="0" width="0.012" height="1" fill="#8f6426" opacity="0.5" />
              <rect x="0.02" y="0" width="0.01" height="1" fill="#fff0c4" opacity="0.3" />
            </pattern>
          ))}
          {/* Red roofing shingles on the wellness roofs */}
          {[[ids.shinglesTall, wellRoofPlane(WELL_TALL)], [ids.shinglesLow, wellRoofPlane(WELL_LOW)]].map(([id, on]) => (
            <pattern key={id} id={id} patternUnits="userSpaceOnUse" width="0.8" height="0.6" patternTransform={`matrix(${on.matrix()})`}>
              <path d="M0 0 H0.8 M0 0.3 H0.8 M0 0 V0.3 M0.4 0.3 V0.6" stroke="#5f1a22" strokeOpacity="0.55" strokeWidth="0.022" fill="none" />
              <rect x="0.42" y="0.02" width="0.36" height="0.26" fill="#ffffff" opacity="0.035" />
            </pattern>
          ))}
          {/* White bricks on the chimney */}
          {[[ids.brick, CHIMNEY_OUT, "#f1f0ea"], [ids.brick2, CHIMNEY_END, "#d3d2ca"]].map(([id, on, color]) => (
            <pattern key={id} id={id} patternUnits="userSpaceOnUse" width="0.5" height="0.24" patternTransform={`matrix(${on.matrix()})`}>
              <rect x="0" y="0" width="0.5" height="0.24" fill={color} />
              <path d="M0 0.12 H0.5 M0 0.24 H0.5 M0.25 0 V0.12 M0 0.12 V0.24 M0.5 0.12 V0.24" stroke="#8d8d86" strokeOpacity="0.6" strokeWidth="0.018" />
            </pattern>
          ))}
          {/* Roof tiles and snow guards, drawn in roof units and mapped onto each roof plane */}
          {[[ids.tilesB, ROOF_BACK], [ids.tilesF, ROOF_FRONT]].map(([id, on]) => (
            <pattern key={id} id={id} patternUnits="userSpaceOnUse" width="0.5" height="0.42" patternTransform={`matrix(${on.matrix()})`}>
              <path d="M0 0.42 L0 0.06 Q0.25 -0.02 0.5 0.06 L0.5 0.42" fill="none" stroke="#5e2510" strokeOpacity="0.45" strokeWidth="0.035" />
              <path d="M0.02 0.36 Q0.25 0.3 0.48 0.36" fill="none" stroke="#f0a27a" strokeOpacity="0.18" strokeWidth="0.03" />
            </pattern>
          ))}
          {[[ids.hooksB, ROOF_BACK], [ids.hooksF, ROOF_FRONT]].map(([id, on]) => (
            <pattern key={id} id={id} patternUnits="userSpaceOnUse" width="1.15" height="1.05" patternTransform={`matrix(${on.matrix()})`}>
              <rect x="0.5" y="0.44" width="0.08" height="0.14" rx="0.02" fill="#c9cdcd" opacity="0.75" />
              <rect x="0.5" y="0.55" width="0.08" height="0.03" fill="#6b6f70" opacity="0.75" />
            </pattern>
          ))}
        </defs>

        {/* Ambient light, ground slab and shadow */}
        <ellipse cx="290" cy="225" rx="290" ry="205" fill={`url(#${ids.glow})`} />
        <Face n={Z} pts={[[L + 1.4, -2.6, 0], [L + 1.4, yWell + 0.8, 0], [-1.2, yWell + 0.8, 0], [-1.2, -2.6, 0]]} fill={`url(#${ids.slab})`} />
        {boxSides(-1.2, L + 1.4, -2.6, yWell + 0.8, -0.35, 0).map((side) => (
          <Face key={side.n.join()} n={side.n} pts={side.pts} fill={side.n[0] ? "#0d1d1e" : "#0a1718"} />
        ))}
        {/* Paths to the entry door and the door in the back wall */}
        <Face n={Z} pts={[[ENTRY.x2, -1.5, 0.01], [L + 1.4, -1.5, 0.01], [L + 1.4, -0.5, 0.01], [ENTRY.x2, -0.5, 0.01]]} fill="#c9c6bb" opacity="0.3" />
        <Face n={Z} pts={[[6.2, D, 0.01], [7.1, D, 0.01], [7.1, D + 0.8, 0.01], [6.2, D + 0.8, 0.01]]} fill="#c9c6bb" opacity="0.22" />
        <Face n={Z} pts={[[6.2, D + 0.8, 0.01], [L + 1.4, D + 0.8, 0.01], [L + 1.4, D + 1.8, 0.01], [6.2, D + 1.8, 0.01]]} fill="#c9c6bb" opacity="0.22" />
        <polygon
          points={poly([[L - 0.5, 0.6, 0], [L + 1.2, 0.8, 0], [L + 1.2, yWell + 0.6, 0], [L - 0.5, yWell + 0.6, 0]])}
          fill="#020807"
          opacity="0.5"
          filter={`url(#${ids.blur})`}
        />

        {/* Entry porch (in front of the front wall; mostly hidden behind the house from here) */}
        {boxSides(ENTRY.x1, ENTRY.x2, -ENTRY.depth, 0, 0, ENTRY.h).filter((side) => side.n[1] !== 1).map((side) => (
          <Face key={side.n.join()} n={side.n} pts={side.pts} fill="#c97f2a" />
        ))}
        {faces(X) && (
          <g>
            {/* Glazing and the door on the side facing the gable */}
            <polygon points={poly(ENTRY_SIDE.rect(-ENTRY.depth + 0.1, -0.1, 2.35, ENTRY.h - 0.12))} fill={`url(#${lit ? ids.glassLit : ids.glass})`} />
            <polygon points={poly(ENTRY_SIDE.rect(-ENTRY.depth / 2 - 0.04, -ENTRY.depth / 2 + 0.04, 2.35, ENTRY.h - 0.12))} fill="#c97f2a" />
            <polygon points={poly(ENTRY_SIDE.rect(-ENTRY.depth + 0.12, -0.55, 0.05, 2.2))} fill="#d99137" />
            <polygon points={poly(ENTRY_SIDE.rect(-ENTRY.depth + 0.22, -0.65, 1.15, 2.1))} fill={`url(#${lit ? ids.glassLit : ids.glass})`} />
            <polygon points={poly(ENTRY_SIDE.rect(-0.45, -0.1, 0.1, 2.2))} fill={`url(#${lit ? ids.glassLit : ids.glass})`} />
            <polygon points={poly(ENTRY_SIDE.rect(-0.8, -0.72, 1.0, 1.1))} fill="#5b5f5e" />
          </g>
        )}
        {boxSides(ENTRY.x1 - 0.1, ENTRY.x2 + 0.1, -ENTRY.depth - 0.12, 0, ENTRY.h, ENTRY.h + 0.16).filter((side) => side.n[1] !== 1).map((side) => (
          <Face key={side.n.join()} n={side.n} pts={side.pts} fill="#2a2e2e" />
        ))}
        <Face
          n={Z}
          pts={[[ENTRY.x1 - 0.1, -ENTRY.depth - 0.12, ENTRY.h + 0.16], [ENTRY.x2 + 0.1, -ENTRY.depth - 0.12, ENTRY.h + 0.16], [ENTRY.x2 + 0.1, 0, ENTRY.h + 0.16], [ENTRY.x1 - 0.1, 0, ENTRY.h + 0.16]]}
          fill="#3b4141"
        />
        {/* Downpipe at the front corner */}
        <polyline points={poly([[9.85, -0.12, EZ - T - 0.1], [9.85, -0.12, 0.2]])} fill="none" stroke="#cfd3d3" strokeWidth="3.5" strokeLinecap="round" />

        {/* House walls */}
        <Face n={X} pts={[[L, 0, 0], [L, D, 0], [L, D, H], gableTop, [L, 0, H]]} fill={`url(#${ids.gable})`} />
        <Face n={Y} pts={[[0, D, 0], [L, D, 0], [L, D, H], [0, D, H]]} fill={`url(#${ids.back})`} />
        {faces(X) && (
          <g>
            <polygon points={poly(GABLE.rect(0, D, 0, 0.45))} fill="#8d918e" />
            <polygon points={poly([[L + E, 0, H], gableTop, [L + E, D, H], [L + E, D, H - 0.55], [L + E, D / 2, RIDGE - T - 0.55], [L + E, 0, H - 0.55]])} fill={`url(#${ids.eaveShadow})`} />
            <Window on={GABLE} u1={1.3} u2={2.6} v1={1.6} v2={3.3} ids={ids} lit={lit} />
            <Window on={GABLE} u1={4.0} u2={5.0} v1={1.8} v2={3.2} ids={ids} lit={lit} />
            <Window on={GABLE} u1={2.7} u2={3.8} v1={5.2} v2={6.6} ids={ids} lit={lit} />
            {/* Purlin ends under the gable overhang */}
            {[1.1, 2.3, 4.2, 5.4].map((y) => {
              const z = H + (y <= D / 2 ? y : D - y) * SLOPE - T - 0.3;
              return <polygon key={y} points={poly(plane([L + OV_X * 0.8, 0, 0], Y, Z).rect(y - 0.13, y + 0.13, z, z + 0.26))} fill="#4a2f1b" />;
            })}
          </g>
        )}
        {faces(Y) && (
          <g>
            <polygon points={poly(BACK.rect(0, L, 0, 0.45))} fill="#7f837f" />
            <polygon points={poly(BACK.rect(0, L, H - 0.75, H))} fill={`url(#${ids.eaveShadow})`} />
            {/* Door and small window between the chimney and the wellness */}
            <polygon points={poly(BACK.rect(6.1, 7.2, 0.05, 2.3))} fill="#eef0ea" />
            <polygon points={poly(BACK.rect(6.2, 7.1, 0.05, 2.2))} fill="#b5742c" />
            <polygon points={poly(BACK.rect(6.35, 6.95, 1.2, 2.0))} fill={`url(#${lit ? ids.glassLit : ids.glass})`} />
            <polygon points={poly(BACK.rect(6.3, 6.4, 1.0, 1.1))} fill="#5b5f5e" />
            <Window on={BACK} u1={4.4} u2={5.1} v1={1.9} v2={2.75} ids={ids} lit={lit} mullion={false} />
          </g>
        )}

        {/* White brick chimney column on the back wall, below the eave */}
        {chimneyColumn}

        {/* Wellness, far part first: the taller part, its band of windows above the low roof */}
        <Face n={Y} pts={WELL_OUT.rect(WELL_TALL.x1, WELL_TALL.x2, 0, WELL_TALL.hOut)} fill={`url(#${ids.woodOut})`} />
        <Face
          n={X}
          pts={[[WELL_TALL.x2, WELL_Y, 0], [WELL_TALL.x2, yWell, 0], [WELL_TALL.x2, yWell, WELL_TALL.hOut], [WELL_TALL.x2, WELL_Y, WELL_TALL.hWall]]}
          fill={`url(#${ids.woodSide})`}
        />
        {faces(Y) && <Window on={WELL_OUT} u1={1.5} u2={2.05} v1={2.25} v2={2.95} ids={ids} lit={lit} frame="#f2f2ee" mullion={false} />}
        {faces(X) &&
          [[0.18, 1.03], [1.38, 2.23], [2.58, 3.43]].map(([from, to]) => {
            const y = WELL_Y + (from + to) / 2;
            const middle = (wallHeight(WELL_TALL, y) + wallHeight(WELL_LOW, y) + ROOF_THICKNESS) / 2;
            return <Window key={from} on={WELL_BAND} u1={WELL_Y + from} u2={WELL_Y + to} v1={middle - 0.28} v2={middle + 0.28} ids={ids} lit={lit} frame="#f2f2ee" mullion={false} />;
          })}
        <ShedRoof
          part={WELL_TALL}
          x1={WELL_TALL.x1}
          edges={[{ y1: WELL_Y, y2: yWell + 0.15, x: WELL_TALL.x2 }]}
          felt="#8e2b33"
          shingles={`url(#${ids.shinglesTall})`}
          fascia="#3d4144"
          eave="#2b2e30"
        />

        {/* Wellness, near part: the low part, stepping out towards the gable */}
        <Face n={Y} pts={WELL_OUT.rect(WELL_LOW.x1, WELL_LOW_FAR_X, 0, WELL_LOW.hOut)} fill={`url(#${ids.woodOut})`} />
        <Face
          n={X}
          pts={[[WELL_LOW_NEAR_X, WELL_Y, 0], [WELL_LOW_NEAR_X, WELL_STEP_Y, 0], [WELL_LOW_NEAR_X, WELL_STEP_Y, wallHeight(WELL_LOW, WELL_STEP_Y)], [WELL_LOW_NEAR_X, WELL_Y, WELL_LOW.hWall]]}
          fill={`url(#${ids.woodSide})`}
        />
        <Face
          n={X}
          pts={[[WELL_LOW_FAR_X, WELL_STEP_Y, 0], [WELL_LOW_FAR_X, yWell, 0], [WELL_LOW_FAR_X, yWell, WELL_LOW.hOut], [WELL_LOW_FAR_X, WELL_STEP_Y, wallHeight(WELL_LOW, WELL_STEP_Y)]]}
          fill={`url(#${ids.woodSide})`}
        />
        {faces(Y) && (
          <g>
            <polygon points={poly(WELL_OUT.rect(WELL_LOW_FAR_X - 0.08, WELL_LOW_FAR_X, 0, WELL_LOW.hOut))} fill="#8a5a2b" />
            <Window on={WELL_OUT} u1={4.25} u2={4.7} v1={1.0} v2={1.75} ids={ids} lit={lit} frame="#f2f2ee" mullion={false} />
          </g>
        )}
        <ShedRoof
          part={WELL_LOW}
          x1={WELL_LOW.x1}
          edges={[{ y1: WELL_Y, y2: WELL_STEP_Y, x: WELL_LOW_NEAR_X }, { y1: WELL_STEP_Y, y2: yWell + 0.15, x: WELL_LOW_FAR_X }]}
          felt="#8e2b33"
          shingles={`url(#${ids.shinglesLow})`}
          fascia="#3d4144"
          eave="#2b2e30"
        />

        {/* Main roof: visible planes with tiles and snow guards, thickness on the edges */}
        <Face n={[0, 1, 0]} pts={[[-OV_X, D + OV_Y, EZ - T], [L + OV_X, D + OV_Y, EZ - T], [L + OV_X, D + OV_Y, EZ], [-OV_X, D + OV_Y, EZ]]} fill="#5a2a14" />
        <Face n={[0, -1, 0]} pts={[[-OV_X, -OV_Y, EZ - T], [L + OV_X, -OV_Y, EZ - T], [L + OV_X, -OV_Y, EZ], [-OV_X, -OV_Y, EZ]]} fill="#5a2a14" />
        <Face n={X} pts={[[L + OV_X, -OV_Y, EZ - T], [L + OV_X, D / 2, RIDGE - T], [L + OV_X, D / 2, RIDGE], [L + OV_X, -OV_Y, EZ]]} fill="#4e2a17" />
        <Face n={X} pts={[[L + OV_X, D + OV_Y, EZ - T], [L + OV_X, D / 2, RIDGE - T], [L + OV_X, D / 2, RIDGE], [L + OV_X, D + OV_Y, EZ]]} fill="#43230f" />
        {[[roofBackPts, nRoofBack, ids.tilesB, ids.hooksB], [roofFrontPts, nRoofFront, ids.tilesF, ids.hooksF]].map(
          ([pts, n, tiles, hooks]) =>
            faces(n) && (
              <g key={tiles}>
                <polygon points={poly(pts)} fill={`url(#${ids.roof})`} />
                <polygon points={poly(pts)} fill={`url(#${tiles})`} />
                <polygon points={poly(pts)} fill={`url(#${hooks})`} />
                <polygon points={poly(pts)} fill={`url(#${ids.roofShade})`} />
              </g>
            )
        )}
        <polyline points={poly([[-OV_X, D / 2, RIDGE], [L + OV_X, D / 2, RIDGE]])} fill="none" stroke="#7d3519" strokeWidth="4" strokeLinecap="round" />
        <polyline points={poly([[-OV_X, D / 2, RIDGE + 0.05], [L + OV_X, D / 2, RIDGE + 0.05]])} fill="none" stroke="#e08a5f" strokeOpacity="0.5" strokeWidth="1" strokeLinecap="round" />
        {/* Gutters */}
        {[-OV_Y - 0.08, D + OV_Y + 0.08].map((y) => (
          <polyline key={y} points={poly([[-OV_X, y, EZ - T - 0.05], [L + OV_X, y, EZ - T - 0.05]])} fill="none" stroke="#d5d9d9" strokeWidth="3.5" strokeLinecap="round" />
        ))}

        {/* Chimney stack through the roof: grey flashing, white bricks, cap and cowl */}
        <polygon
          points={poly([
            [cx1 - flash, cy2 + flash + 0.2, roofHeightAt(cy2 + flash + 0.2)],
            [cx2 + flash, cy2 + flash + 0.2, roofHeightAt(cy2 + flash + 0.2)],
            [cx2 + flash, cy1, roofHeightAt(cy1)],
            [cx1 - flash, cy1, roofHeightAt(cy1)],
          ])}
          fill="#8b9296"
        />
        <Face n={Y} pts={[[cx1, cy2, roofHeightAt(cy2)], [cx2, cy2, roofHeightAt(cy2)], [cx2, cy2, ctop], [cx1, cy2, ctop]]} fill={`url(#${ids.brick})`} />
        <Face n={X} pts={[[cx2, cy2, roofHeightAt(cy2)], [cx2, cy1, roofHeightAt(cy1)], [cx2, cy1, ctop], [cx2, cy2, ctop]]} fill={`url(#${ids.brick2})`} />
        {boxSides(cx1 - 0.06, cx2 + 0.06, cy1 - 0.06, cy2 + 0.06, ctop, ctop + 0.12).map((side) => (
          <Face key={side.n.join()} n={side.n} pts={side.pts} fill="#c9cac4" />
        ))}
        <Face n={Z} pts={[[cx1 - 0.06, cy1 - 0.06, ctop + 0.12], [cx2 + 0.06, cy1 - 0.06, ctop + 0.12], [cx2 + 0.06, cy2 + 0.06, ctop + 0.12], [cx1 - 0.06, cy2 + 0.06, ctop + 0.12]]} fill="#e7e7e1" />
        {boxSides((cx1 + cx2) / 2 - 0.16, (cx1 + cx2) / 2 + 0.16, (cy1 + cy2) / 2 - 0.16, (cy1 + cy2) / 2 + 0.16, ctop + 0.12, ctop + 0.42).map((side) => (
          <Face key={side.n.join()} n={side.n} pts={side.pts} fill={side.n[0] ? "#7f8588" : "#9aa0a3"} />
        ))}
        <Face
          n={Z}
          pts={[[(cx1 + cx2) / 2 - 0.22, (cy1 + cy2) / 2 - 0.22, ctop + 0.42], [(cx1 + cx2) / 2 + 0.22, (cy1 + cy2) / 2 - 0.22, ctop + 0.42], [(cx1 + cx2) / 2 + 0.22, (cy1 + cy2) / 2 + 0.22, ctop + 0.42], [(cx1 + cx2) / 2 - 0.22, (cy1 + cy2) / 2 + 0.22, ctop + 0.42]]}
          fill="#b4babd"
        />
      </svg>

      {markers.map((marker) => {
        const [x, y] = project(...marker.at);
        return (
          <div
            key={marker.id}
            aria-label={marker.label}
            style={{ position: "absolute", left: `${(x / VIEW_W) * 100}%`, top: `${(y / VIEW_H) * 100}%`, transform: "translate(-10px, -50%)", display: "flex", alignItems: "center", gap: "8px" }}
          >
            <span
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, width: "20px", height: "20px", borderRadius: "50%", boxSizing: "border-box",
                border: `2px solid ${marker.active ? "#fde68a" : "rgba(255,255,255,0.8)"}`,
                background: marker.active ? "#fbbf24" : "rgba(255,255,255,0.3)",
                boxShadow: "0 0 12px rgba(255,255,255,0.45)",
              }}
            >
              <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "white" }} />
            </span>
            {marker.label && (
              <span style={{ whiteSpace: "nowrap", borderRadius: "16px", border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.1)", padding: "6px 12px", fontSize: "12px", color: "white", backdropFilter: "blur(12px)" }}>
                <span style={{ display: "block", color: "rgba(255,255,255,0.7)" }}>{marker.label}</span>
                {marker.value && <span style={{ display: "block", fontWeight: 600 }}>{marker.value}</span>}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
